'use client';

import React, { useEffect, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { colors, typography } from '@/styles/theme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/lib/toast';
import { useTranslation } from '@/hooks/useTranslation';

export interface SimpleColumn<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  width?: string;
}

export interface SimpleListPageProps<T> {
  title: string;
  description?: string;
  columns: SimpleColumn<T>[];
  load: (params?: any) => Promise<T[] | { items: T[] }>;
  searchPlaceholder?: string;
  onRowClick?: (item: T) => void;
  toolbar?: React.ReactNode;
  emptyText?: string;
}

/**
 * 通用列表页 — 用于 platform-master 主数据 + L3 业务表的快速渲染。
 * 不做分页 UI（直接显示返回的 items），不做编辑（编辑跳详情）。
 */
export function SimpleListPage<T extends { id: string }>({
  title,
  description,
  columns,
  load,
  searchPlaceholder,
  onRowClick,
  toolbar,
  emptyText,
}: SimpleListPageProps<T>) {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const result = await load(search ? { search } : undefined);
      setItems(Array.isArray(result) ? result : result.items);
    } catch (err: any) {
      toast.error(err?.message ?? rm.messages.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      <div className="bg-white border-b px-6 h-12 flex items-center justify-between" style={{ borderColor: colors.border }}>
        <div>
          <h1 className={typography.h4}>{title}</h1>
          {description && <span className={`${typography.caption} ml-3`}>{description}</span>}
        </div>
        <div className="flex items-center gap-2">
          {toolbar}
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="w-4 h-4 mr-1" />
            {rm.actions.refresh}
          </Button>
        </div>
      </div>

      <div className="px-6 py-3 flex items-center gap-3 bg-white border-b" style={{ borderColor: colors.border }}>
        <Input
          placeholder={searchPlaceholder ?? rm.placeholders.searchCodeName}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refresh()}
          className="max-w-sm"
        />
        <Button size="sm" onClick={refresh}>{rm.actions.search}</Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-lg overflow-hidden" style={{ backgroundColor: colors.bgPrimary, border: `1px solid ${colors.border}` }}>
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: colors.textTertiary }}>
              {emptyText ?? rm.messages.noDataMessage}
            </div>
          ) : (
            <Table className="[&_th]:h-11 [&_th]:px-3 [&_th]:whitespace-nowrap [&_th]:text-xs [&_th]:font-semibold [&_td]:py-2.5 [&_td]:px-3 [&_td]:whitespace-nowrap [&_tbody_tr]:!border-0 [&_tbody_tr:nth-child(even)]:bg-[#fafbfc]">
              <TableHeader className="bg-[#f7f8fa] [&_tr]:border-b [&_tr]:border-[#ebedf0]">
                <TableRow className="hover:bg-transparent border-0">
                  {columns.map((c) => (
                    <TableHead key={c.key} style={{ width: c.width, color: colors.textSecondary }}>
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                    className={
                      onRowClick
                        ? 'cursor-pointer transition-colors hover:!bg-[#f5f8ff]'
                        : ''
                    }
                  >
                    {columns.map((c) => (
                      <TableCell key={c.key}>
                        {c.render ? c.render(item) : (item as any)[c.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

/** Stage / status 徽章 */
export function Badge({
  text,
  color = '#888',
  bg = '#f0f0f0',
}: {
  text: string;
  color?: string;
  bg?: string;
}) {
  return (
    <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: bg, color }}>
      {text}
    </span>
  );
}
